import { useCallback, useEffect, useMemo, useState } from "react";

import type {
	MetaDataPostMeetingAnalysisResponseStreamJson,
	PostMeetingAnalysisSettings,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useInternalApiUser } from "../../../../api/hooks";
import { useAiQuery, useAiSettings } from "../../../../api/hooks/defs/ai";
import { useMeetingTranscript } from "../../../../api/hooks/defs/meetings";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { useMeetingContext } from "../context";

const isSuccessResponse = (d: unknown): d is MetaDataPostMeetingAnalysisResponseStreamJson =>
	typeof d === "object" && d !== null && "status" in d && "data" in d;

const isErrorResponse = (d: unknown): d is UserErrorResponseJson =>
	typeof d === "object" && d !== null && "message" in d && !("data" in d);

type UsePostMeetingAnalysisResult = {
	data: MetaDataPostMeetingAnalysisResponseStreamJson | null;
	isLoading: boolean;
	error: UserErrorResponseJson | null;
	isVisible: boolean;
	isOpen: boolean;
	handleOpen: () => void;
	handleClose: () => void;
	handleRegenerate: () => void;
	directReportId: string | undefined;
	postMeetingAnalysisSettings: PostMeetingAnalysisSettings | null;
};

export const usePostMeetingAnalysis = (): UsePostMeetingAnalysisResult => {
	const { meeting, workspace, userId, isManagerDirectReportMeeting } = useMeetingContext();

	const [isOpen, setIsOpen] = useState(false);
	const [recreateCount, setRecreateCount] = useState(0);

	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: ffCoaching } = useFeatureFlagQuery("ff-is-coaching-enabled", false);
	const { value: ffMeetings } = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	const { data: aiSettingsData } = useAiSettings();

	const postMeetingAnalysisEnabled = aiSettingsData?.postMeetingAnalysisEnabled ?? false;
	// Exposed so the panel/copy text hide sections an admin disabled after a cached analysis was generated
	const postMeetingAnalysisSettings = aiSettingsData?.postMeetingAnalysisSettings ?? null;

	const isCompletedMeeting = meeting?.status === "completed";

	// Find the direct report participant (the non-current user)
	const directReportId = useMemo(() => {
		if (!workspace?.participants || !userId) return undefined;
		const other = workspace.participants.find((p) => p.userId !== userId);
		return other?.userId;
	}, [workspace?.participants, userId]);

	// Post-meeting analysis is only shown to the manager side of the relationship.
	// Since isManagerDirectReportMeeting is true for both participants,
	// we need to verify the current user is the manager by checking their direct reports.
	const { data: currentUserData } = useInternalApiUser(userId, {
		enabled: Boolean(isManagerDirectReportMeeting) && Boolean(userId),
	});
	const isCurrentUserManager = useMemo(() => {
		if (!isManagerDirectReportMeeting || !directReportId) return false;
		const directReports = currentUserData?.directReports || [];
		return directReports.some((report) => report._id === directReportId);
	}, [isManagerDirectReportMeeting, directReportId, currentUserData?.directReports]);

	const passesNonTranscriptChecks = Boolean(
		ffAiEnabled &&
		ffCoaching &&
		ffMeetings &&
		postMeetingAnalysisEnabled &&
		isCurrentUserManager &&
		isCompletedMeeting
	);

	// A transcript (pasted or retrieved) is a hard prerequisite for the analysis.
	// Only fetch once the other checks pass — non-participants (e.g. admins) lack
	// meetings:viewTranscript and the request would 403.
	const { data: transcript } = useMeetingTranscript(passesNonTranscriptChecks ? meeting?._id || "" : "");
	const hasTranscript = Boolean(transcript?.content?.trim());

	const isVisible = passesNonTranscriptChecks && hasTranscript;

	// AI query for post-meeting analysis
	const {
		data: queryData,
		isLoading,
		isFetching,
		isRefetching,
	} = useAiQuery<MetaDataPostMeetingAnalysisResponseStreamJson | UserErrorResponseJson>({
		type: "post-meeting-analysis",
		meetingId: meeting?._id || "",
		workspaceId: workspace?._id || "",
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
		enabled: isOpen && isVisible,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching) return true;
		if (isSuccessResponse(queryData)) {
			return queryData.status === "in_progress";
		}
		return false;
	}, [queryData, isLoading, isRefetching]);

	const isError = useMemo((): boolean => {
		if (!queryData || inProgress) return false;
		return isErrorResponse(queryData);
	}, [queryData, inProgress]);

	const data = useMemo((): MetaDataPostMeetingAnalysisResponseStreamJson | null => {
		if (!queryData || isError || inProgress) return null;
		if (isSuccessResponse(queryData)) return queryData;
		return null;
	}, [queryData, isError, inProgress]);

	const error = useMemo((): UserErrorResponseJson | null => {
		if (!isError || !queryData) return null;
		if (isErrorResponse(queryData)) return queryData;
		return null;
	}, [queryData, isError]);

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);
	const handleRegenerate = useCallback(() => setRecreateCount((prev) => prev + 1), []);

	// Reset recreateCount when panel closes to ensure cached results are fetched on reopen
	useEffect(() => {
		if (!isOpen) {
			setRecreateCount(0);
		}
	}, [isOpen]);

	return {
		data,
		isLoading: isLoading || isFetching || inProgress,
		error,
		isVisible,
		isOpen,
		handleOpen,
		handleClose,
		handleRegenerate,
		directReportId,
		postMeetingAnalysisSettings,
	};
};
